import { CHAIN_IDS, NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { LiquidityProviderAvatar } from '@/ui/Avatars/LiquidityProviderAvatar';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { LiquiditySource, LiquiditySourceMultiHop } from '@/utils/liquidity-sources';

type Sources = LiquiditySource[] | LiquiditySourceMultiHop[] | undefined;

interface Props {
  availableSourceCount: number;
  isLoading: boolean;
  isWrap: boolean;
  tooltipTrigger: React.ReactElement;
  liquiditySources?: LiquiditySource[];
  chainId: number;
}

export const RouteDetailsTooltip = ({
  chainId,
  isLoading,
  tooltipTrigger,
  liquiditySources,
  isWrap,
}: Props) => {
  const networkName = NETWORK_NAME_PER_CHAIN_ID[chainId];

  if (isWrap) {
    return (
      <MatchaTooltip>
        <MatchaTooltipTrigger>{tooltipTrigger}</MatchaTooltipTrigger>
        <MatchaTooltipContent>
          <p>
            Executed directly on the {networkName} network&apos;s wrapped native token contract.
          </p>
        </MatchaTooltipContent>
        <MatchaTooltipPopover side="top" sideOffset={5} />
      </MatchaTooltip>
    );
  }

  if (isLoading) {
    return tooltipTrigger;
  }

  // we only want to display the top three sources
  const sortedSources = liquiditySources
    ?.sort((a, b) => Number(b.proportion) - Number(a.proportion))
    .slice(0, 3) as Sources;

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>{tooltipTrigger}</MatchaTooltipTrigger>
      <MatchaTooltipContent>
        <div className="w-full flex flex-col gap-2">
          {sortedSources?.map((source, index) => (
            <div className="text-token-gray-800 flex gap-2 justify-between" key={source.name}>
              <div className="flex justify-start items-center">
                <LiquidityProviderAvatar name={source.name} size={20} />
                <span className="ml-2">
                  {/* '0x' means RFQ on EVM; on Solana it is the 0x race leg's fallback source label */}
                  {sortedSources[index].name === '0x' && chainId !== CHAIN_IDS.SOLANA
                    ? '0x RFQ'
                    : sortedSources[index].name.replace(/_/g, ' ')}
                </span>
              </div>
              <span className="text-token-gray-1600">
                {` ${(Number(source.proportion) * 100).toFixed(2)}`}%{' '}
              </span>
            </div>
          ))}
          {liquiditySources && liquiditySources.length > 3 && (
            <p className="text-token-gray-800">+{liquiditySources.length - 3} more</p>
          )}
        </div>
      </MatchaTooltipContent>
      <MatchaTooltipPopover side="top" className="w-62 py-3" />
    </MatchaTooltip>
  );
};
